import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  InputBase,
  Divider,
  Chip,
} from "@mui/material";
import {
  Search,
  CameraAlt,
  Refresh,
  LocalShippingOutlined,
  History,
  Close,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import TrackingDetail from "components/TrackingDetail";
import TrackingSummaryItem from "components/TrackingSummaryItem";
import Scanner from "components/Scanner";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { getScanText } from "utils";
import { SG, BG, BODY_FONT, PURPLE_LIGHT } from "theme/publicDarkTokens";

const MAX_TRACKING = 10;
const STORAGE_KEY = "winner_recent_tracking";
const MAX_RECENT = 10;

const parseInput = (input) =>
  input
    .split(/[,\n;]+/)
    .map((s) => getScanText(s.trim()))
    .filter(Boolean)
    .slice(0, MAX_TRACKING);

// ── Main page ─────────────────────────────────────────────────────────────────
const TrackPackagePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [inputValue, setInputValue] = useState("");
  const [trackingIds, setTrackingIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  const saveToRecent = (ids) => {
    if (!ids.length) return;
    setRecentSearches((prev) => {
      const updated = [...new Set([...ids, ...prev])].slice(0, MAX_RECENT);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const removeFromRecent = (tracking) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((t) => t !== tracking);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const clearRecent = () => {
    setRecentSearches([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Load tracking-id(s) from URL — backward compat with old ?tracking=
  useEffect(() => {
    const oldParam = searchParams.get("tracking");
    if (oldParam) {
      const params = new URLSearchParams();
      params.append("tracking-id", getScanText(oldParam));
      navigate(`/track?${params.toString()}`, { replace: true });
      return;
    }
    const ids = searchParams
      .getAll("tracking-id")
      .map(getScanText)
      .filter(Boolean);
    setTrackingIds(ids);
    setInputValue(ids.join(", "));
  }, [searchParams]);

  const handleSearch = (rawValue = inputValue) => {
    const ids = parseInput(rawValue);
    if (!ids.length) {
      navigate("/track", { replace: true });
      setTrackingIds([]);
      setInputValue("");
      return;
    }
    saveToRecent(ids);
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("tracking-id", id));
    navigate(`/track?${params.toString()}`, { replace: true });
  };

  const parsedCount = parseInput(inputValue).length;
  const secondary = theme.palette.secondary.main;
  const isSingle = trackingIds.length === 1;
  const isMultiple = trackingIds.length > 1;

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" sx={{ background: BG, fontFamily: BODY_FONT }}>
      <PublicHeaderDark />

      <Box
        flex={1}
        sx={{
          position: "relative",
          overflow: "hidden",
          background: BG,
          py: { xs: 5, md: 8 },
        }}
      >
        <DarkHeroBackdrop />
        <Box sx={{ position: "relative", maxWidth: 680, mx: "auto", px: 2 }}>

          {/* Header */}
          <Box textAlign="center" mb={3.5}>
            <Typography
              sx={{ fontFamily: SG, fontSize: isMobile ? 26 : 32, fontWeight: 700, letterSpacing: "-.02em", color: PURPLE_LIGHT }}
            >
              {t("tracking.title")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.5)", mt: 0.75 }}>
              {t("tracking.inputHint")}
            </Typography>
          </Box>

          {/* Unified card — light island, hosts shared TrackingDetail/TrackingSummaryItem components */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              backgroundColor: "#F3EFFC",
              boxShadow: "0 24px 64px rgba(0,0,0,.45), 0 0 56px rgba(124,58,237,.14)",
              border: "1.5px solid rgba(124,58,237,.25)",
            }}
          >
            {/* Search row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `2px solid ${secondary}40`,
              }}
            >
              <Search sx={{ color: theme.palette.text.disabled, flexShrink: 0, alignSelf: "flex-start", mt: "10px" }} />
              <InputBase
                id="trackingNumber"
                placeholder={t("tracking.inputPlaceholder")}
                fullWidth
                multiline
                maxRows={6}
                autoFocus
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (!e.target.value) {
                    setTrackingIds([]);
                    navigate("/track", { replace: true });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSearch(e.target.value);
                  }
                }}
                sx={{ fontSize: "1rem", fontWeight: 500 }}
              />
              {/* Count badge — only visible when typing multiple */}
              {parsedCount > 1 && (
                <Chip
                  label={parsedCount}
                  size="small"
                  color="secondary"
                  sx={{ height: 22, minWidth: 28, fontSize: "0.75rem", fontWeight: 700 }}
                />
              )}
              {isMobile && (
                <IconButton
                  onClick={() => setOpen(true)}
                  size="small"
                  sx={{ color: "text.secondary" }}
                >
                  <CameraAlt fontSize="small" />
                </IconButton>
              )}
              <IconButton
                onClick={() => handleSearch()}
                sx={{
                  bgcolor: secondary,
                  color: "white",
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  flexShrink: 0,
                  "&:hover": { bgcolor: theme.palette.secondary.dark },
                }}
              >
                <Search fontSize="small" />
              </IconButton>
            </Box>

            {/* ── Results ── */}
            {trackingIds.length > 0 ? (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  pt={1.5}
                  pb={0.5}
                >
                  <Typography variant="caption" color="text.secondary">
                    {isMultiple
                      ? `${trackingIds.length} ${t("tracking.trackingCount")}`
                      : <>Tracking: <strong>{trackingIds[0]}</strong></>}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleSearch(inputValue)}
                    sx={{ color: "text.secondary" }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Box>
                <Divider />

                {/* Single → full detail */}
                {isSingle && (
                  <Box
                    sx={{
                      "& > div > .MuiPaper-root": {
                        maxWidth: "100%",
                        borderRadius: 0,
                        boxShadow: "none",
                        backgroundColor: "#F3EFFC",
                        mt: 0,
                      },
                    }}
                  >
                    <TrackingDetail trackingNumber={trackingIds[0]} masked={true} />
                  </Box>
                )}

                {/* Multiple → summary list */}
                {isMultiple && (
                  <Box>
                    {trackingIds.map((id, i) => (
                      <React.Fragment key={id}>
                        <TrackingSummaryItem trackingId={id} masked={true} />
                        {i < trackingIds.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              /* ── No search yet ── */
              <Box py={3} px={2}>
                {recentSearches.length > 0 ? (
                  <>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1.5}
                    >
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <History sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                        >
                          {t("tracking.recentSearches")}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ cursor: "pointer", "&:hover": { color: "text.secondary" } }}
                        onClick={clearRecent}
                      >
                        {t("common.clear")}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={0.75}>
                      {recentSearches.map((tracking) => (
                        <Box
                          key={tracking}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            backgroundColor: "background.paper",
                            border: "1px solid transparent",
                            "&:hover": {
                              border: `1px solid ${secondary}40`,
                              backgroundColor: `${secondary}08`,
                            },
                          }}
                          onClick={() => handleSearch(tracking)}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <History sx={{ fontSize: 14, color: "text.disabled" }} />
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ fontFamily: "monospace" }}
                            >
                              {tracking}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromRecent(tracking);
                            }}
                            sx={{
                              p: 0.25,
                              color: "text.disabled",
                              "&:hover": { color: "text.secondary" },
                            }}
                          >
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                    color="text.disabled"
                  >
                    <LocalShippingOutlined sx={{ fontSize: 48, mb: 1.5, opacity: 0.3 }} />
                    <Typography variant="body2">
                      {t("tracking.noTrackingPrompt")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

        </Box>
      </Box>

      <PublicFooterDark />

      <Scanner
        open={open}
        setOpen={setOpen}
        closeAfterResult={true}
        onDecode={(result) => {
          if (result) {
            const clean = getScanText(result);
            const current = parseInput(inputValue);
            const merged = [...new Set([...current, clean])].slice(0, MAX_TRACKING);
            const newInput = merged.join(", ");
            setInputValue(newInput);
            setOpen(false);
            handleSearch(newInput);
          }
        }}
      />
    </Box>
  );
};

export default TrackPackagePage;
